import { CD_FREQUENCY, DISBURSEMENT_METHODS } from './utils/constants'

export interface AccountAddress {
  addressLine1: string
  addressType: string
  city: string
  country: string
  state: string
  zipCode: string
}

export type UpcomingTransactionResponse = BillPayResponse | TransferResponse

export interface BeneficiaryAddress {
  addressLine1: string
  city: string
  country: string
  postAddressId: number
  state: string
  zipCode: string
}

export interface OtsAccount {
  otsAccountId: string
  otsAccountNickname: string
  otsAccountNumberPvtEncrypt: string
  otsAccountPriority: number
  otsAccountType: string
  otsAvailableBalancePvtEncrypt: number
}

export interface Beneficiary {
  address: BeneficiaryAddress
  allocationPercentage: number
  beneficiaryClassificationCode: string
  beneficiaryId: string
  beneficiaryName?: string
  firstName?: string
  lastName?: string
  middleName?: string
  dateOfBirth?: string
}

export interface RetirementPlan {
  planId: string
  planNumber: string
  planType: string
}

export interface AccountDetails {
  lastRenewalDate?: string
  lastCdMaturityDate?: string
  currentRedemptionAmt?: number
  totalProjectedInterest?: number
  balanceWithInterestAccruedPvtEncrypt?: number
  coverDraftRiskLevel?: number
  takeActionDate?: string
  accountAddress: AccountAddress
  accountIndex: string
  accountNickname: string
  accountNumberPvtEncrypt: string
  accountStatus: string
  accountType: string
  acctClosedDate?: string
  acctOpenDate: string
  allowanceCurrentAmount?: number
  allowanceCustomerIndicator: boolean
  allowanceCustomerReqAmount?: number
  allowanceEligibilityIndicator: boolean
  allowanceEligibilityReasonCd: number
  apy: number
  availableBalancePvtEncrypt: number
  beneficiaries?: Beneficiary[]
  cdInstructionDetails?: CdInstructionDetails
  cdTermDescription?: string
  cdMaturityDatePvtEncrypt?: string
  coverageAmountOptions?: number[]
  manageCdAllowed?: boolean
  manageCdBlocked?: boolean
  currentBalancePvtEncrypt: number
  dailyInterestAccrued: number
  inGracePeriod?: boolean
  gracePeriodDuration?: number
  consecutiveNegBalanceDays?: number
  earlyDirectEligibilityIndicator?: boolean
  helocAvailableBalancePvtEncrypt?: number
  helocBalancePvtEncrypt?: number
  helocCreditLimitPvtEncrypt?: number
  interestAccrued?: number
  interestPriorYear: number
  interestRate: number
  interestRatePvtEncrypt: number
  interestWithheldLastYear: number
  interestWithheldYtd: number
  interestYtd: number
  limitedTransactionsRemaining: number
  nextStatementDate: string
  otsAccounts: OtsAccount[]
  ownerNames: string[]
  ownershipType: string
  productDescription: string
  productId: number
  product?: Product
  retirementAccountIndicator: boolean
  retirementPlan?: RetirementPlan
  transferFromIndicator: boolean
  transferToIndicator: boolean
  totalAllowedRateIncreases?: number
  ryrIncreasesUsed?: number
  ryrAvailableIndicator?: boolean
  cdInstrTransferAccount?: CdInstrTransferAccount
  firstDepositDate?: string
  pendingRyrReqIndicator?: boolean
  productTerm?: string
  productTermDescription?: string
}

export interface UpcomingTransactionsTableModel {
  date: string
  status: string
  amount: string
  description: string
  isCredit: boolean
  isRecurring: boolean
}

export interface UpcomingTransactionsTableRow {
  cell: {
    value: string
    row: {
      original: {
        isRecurring: boolean
        isCredit: boolean
      }
    }
  }
}

export interface BillPayModel {
  transactionEffectiveDate: string
  isCreditTransaction: boolean
  transactionAmount: number
  isBillPay: boolean
  type: string | null
  status: string
  nickName: string
  maskedAccountNumber: string
}

export interface Transfer {
  amount: number
  requestId: string
  status: string
  domainDetails: DomainDetails
  fromAccount: TransferAccount
  toAccount: TransferAccount
  transactionType: string
  frequency: string
}

export interface TransferResponse {
  transfers: Transfer[]
  errors?: Error[]
}

export interface UpcomingTransactionModel {
  transactionEffectiveDate: string
  isCreditTransaction: boolean
  transactionAmount: number
  isBillPay: boolean
  type: string | null
  domain: string
  status: string
  externalAccount: boolean
  nickName: string | undefined
  maskedAccountNumber: string
  frequency: string
}

export interface BillPayment {
  account: BillPayAccount
  amountPvtEncrypt: number
  confirmationNumber: string
  deliveryType: string
  errors: Error[]
  frequency: string
  id: string
  nonAllyPayment: boolean
  paymentDate: string
  paymentDateRevised: boolean
  recurring: boolean
  status: string
}

export interface BillPayAccount {
  accountId: string
  accountNickname?: string
  accountNumberPvtEncrypt: string
  accountOpeningDate: string
  accountStatus: string
  accountType: string
  apy: number
  availableBalancePvtEncrypt: number
  bankName: string
  currentBalancePvtEncrypt: number
  externalAccountIndicator: boolean
  interestYtd: number
  ownershipType: string
  productDescription: string
  productId: number
  rdcEligibilityIndicator: boolean
  retirementIndicator: boolean
  transferFromIndicator: boolean
  transferToIndicator: boolean
}

interface BillPayRecord extends BillPayment {
  payee: Payee
}

interface BillPayRecordGroupHeader {
  attributeType: string
  attributeValue: string
}

export type BillPayResponseItem = {
  record: BillPayRecord[]
  recordGroupHeader: BillPayRecordGroupHeader[]
}

interface BillPayResponseGroup {
  recordGroup: BillPayResponseItem[]
}

export type BillPayResponse = BillPayResponseGroup | Record<string, never>

export interface Payee {
  accountNumPvtEncrypt: string
  earliestPaymentDate: string
  earliestRegularPaymentCutoffTime: string
  id: string
  inProgressPayment: BillPayment
  lastAllyPayment: BillPayment
  scheduledPayment: BillPayment
  paperPaymentEnabled: boolean
  name?: string
  nickname?: string
}

export interface Plan {
  nextRunDate: string
  startDate: string
}

export interface DomainDetails {
  accountId: string
  bankName: string
  deliveryDate?: string
  domain: string
  externalAccountIndicator: boolean
  plan?: Plan
}

export interface TransferAccount {
  availableBalance: number
  currentBalance: number
  nickname: string
  domainDetails: DomainDetails
  accountNumberPvtEncrypt: string
  accountStatus: string
  accountType: string
  isTransferEligibleFrom: true
  isTransferEligibleTo: true
}

export interface TransactionHistoryTableModel {
  id: string
  date: string
  description: string
  amount: number | string | undefined
  balance: number | string | undefined
  sequenceNumber: number
  isDepositedCheck?: boolean | undefined
  transactionEffectiveDate?: string
  isCheck: boolean
  showEdd: boolean
}

export interface NextRequestResponse {
  accountNumberPvtEncrypt: string
  mxOptInIndicator: boolean
  pagination: {
    contentType: string
    idLength: number
    position: string
  }
  recordsToPull: number
}

export interface TransactionResponse {
  accountNumber: string
  accountNumberPvtEncrypt: string
  accountType: string
  transactionHistory:
    | [
        {
          matchedRecordsTotal: number
          nextPageRequestDetails: NextRequestResponse
          transactionStatusType: string
          transactions: [
            {
              checkTransactionIndicator: boolean
              recurringTransferIdentifier: string
              transactionAmountPvtEncrypt: number
              transactionBalancePvtEncrypt: number
              transactionDescription: string
              transactionId: string
              transactionPostingDate: string
              transactionSequenceNumber: number
              transactionStatus: string
              transactionCode: string
              transactionType?: string
              transactionEffectiveDate?: string
            },
          ]
        },
      ]
    | null
}

export interface SearchResults {
  checkTransactionIndicator: boolean
  transactionAmountPvtEncrypt: number
  transactionBalancePvtEncrypt: number
  transactionDescription: string
  transactionDetailUrl: string
  transactionId: string
  transactionPostingDate: string
  transactionSequenceNumber: number
  transactionStatus: string
  transactionType: string
}

export interface fundsAvailability {
  businessDay: number
  amountAvailablePvtEncrypt: number
}

export interface checkHold {
  fundsAvailability: fundsAvailability[]
  holdCode: string
  holdReason: string
}

export interface TransactionDetails {
  checkImageUrl: string
  checkTransactionIndicator: boolean
  comment: string
  disputeStatus?: string
  earlyWithdrawalPenaltyApplied: number
  eligibilityStatus: string
  transactionAmountPvtEncrypt: number
  transactionBalancePvtEncrypt: number
  transactionCheckNumberPvtEncrypt: string | undefined
  transactionCode: string
  transactionDescription: string
  transactionId: string
  transactionPostingDate: string
  transactionSequenceNumber: number
  transactionStatus: string
  transactionType: string
  checkHold?: checkHold
}

export interface SpendingBucketCategory {
  categoryId: string
  categoryName: string
  categoryDisplayOrder: number
  categoryCode?: number
}

export interface SpendingBucketsTransaction {
  accountId: string
  bucketBalanceAfterPost?: number | string
  bucketTransactionId: string
  cleansedTransactionAmount?: number
  detailIndicator?: string
  toolkitBucketId: string
  transactionAmount: number
  transactionDate?: string
  transactionDescription: string
  transactionDetails: SpendingBucketsTransactionDetails
  transactionPostingTime: string
  transactionTypeId: string
  transactionTypeName: string
  overspendAmount: number
  isMoveable: boolean
  merchantId: string | null
  merchantName?: string | null
  isGreyedOut?: boolean
  overSpendIndicator?: 1 | 0 | null
}

export interface SpendingBucketsTransactionDetails {
  covered?: CoveredTransactionDetails[]
  overspend: OverspendTransactionDetails[]
  recategorization: RecategorizationTransactionDetails[]
}

export interface CoveredTransactionDetails {
  toToolkitBucketId: string
  toToolkitBucketName: string
  detailIndicator: string
  originalTransactionAmount: number
  originalTransactionDescription: string
  originalTransactionDate: string
  created: string
}

export interface OverspendTransactionDetails {
  amount: number
  bucketTransactionId: string
  bucketTransactionDetailId: string
  fromBucketBalance: number
  fromToolkitBucketId: string
  fromToolkitBucketName: string
  toBucketBalance: number
  toToolkitBucketId: string
  toToolkitBucketName: string
  totalOverspentAmount: number
}

export interface RecategorizationTransactionDetails {
  originalTransactionAmount: number
  bucketTransactionId: string
  bucketTransactionDetailId: string
  fromToolkitBucketId: string
  fromToolkitBucketName: string
  toToolkitBucketId: string
  toToolkitBucketName: string
  originalTransactionDescription: string
  originalTransactionDate: string
  created: string
}

export interface SpendingBucketsTransactionsResponse {
  toolkitBucketId: string
  bucketBalance: number
  bucketCategoryName?: string
  totalPages: number
  totalRecords: number
  bucketBalanceLastUpdateDate?: string
  pendingTransactions: SpendingBucketsTransaction[]
  postedTransactions: SpendingBucketsTransaction[]
}

export interface Merchant {
  merchantId: string
  taggedToBucket: boolean
  merchantName: string
  merchantCode?: number
  bucketName?: string
  bucketId?: string
}

export interface SpendingBucketFrequency {
  frequencyTypeId?: string
  frequencyTypeCode?: string
  frequencyTypeName?: string
}

export interface SpendingBucketResponse {
  customBucketTotal?: number
  buckets: SpendingBucket[]
}

export interface SpendingBucket {
  autoDistributeIndicator: boolean
  bucketBalance: number
  categoryId: string
  bucketCreationDate?: string
  bucketMerchants?: Merchant[]
  bucketName: string
  bucketTargetAmount: string
  bucketStatus: string
  bucketTargetDate?: string
  frequencyCode: string
  frequencyName?: string
  isUnallocatedBucket: boolean
  nextExpenseDescription?: string
  overspendIndicator?: boolean
  overspendPriority: number
  toolkitBucketId: string
  updatedByUser?: string
  id?: string
  categoryCode?: number
}

export interface Merchants {
  merchantResponse: Merchant[]
}

export interface CdInstructionDetails {
  currentTermInterestOptions: TermInterestOptions
  nextTermInstruction: string
  transferAccountId?: string
  defaultRenewalProductId?: string
  transferBankName?: string
  transferAmount?: number
  transferAccountNumber?: number
  transferAccountJointOwner?: string
  isNextTermOptionsAvailable?: boolean
  nextTermProductId?: string
  nextTermInterestOptions?: TermInterestOptions
  nextTermProductTermDescription?: string
}

export interface CdInstrTransferAccount {
  accountNickname?: string
  bankName?: string
  externalAccountIndicator?: boolean
}

export interface TermInterestOptions {
  disbursementAccountId: string
  disbursementAccountNumber: string
  disbursementOption: string
  postingFrequency: string
  disbursementAccountJointOwner?: string
}

export interface Product {
  accountType: string
  productDescription: string
  productId: string
  productName: string
  promotional: boolean
  retirementProduct: boolean
  terms: ProductTerms[]
}

export interface ProductTerms {
  interestRates: InterestRate[]
  productTermDescription: string
  term: string
}

export interface InterestRate {
  apy: number
  higherAmountLimit: number
  interestRateType: string
  lowerAmountLimit: number
  rate: number
}

export interface CheckDetails {
  accountNumber?: string
  amount?: string
  postingDate?: string
  postingTime?: string
  type?: string
  frontCheckImage?: string
  backCheckImage?: string
  accountNickname?: string
  productDescription?: string
  availableBalance?: string
}

export interface SpendingBucketFormValues {
  bucketName: string
  category: string
  targetAmount: number | null
  frequency: string
  startDate: string
  autoDistributeIndicator: string
  initialFundAmount?: number | null
  merchants: Merchant[]
}

export type CdFrequency = keyof typeof CD_FREQUENCY

export type DisbursementMethod = keyof typeof DISBURSEMENT_METHODS

export interface StkBucket {
  accountBucketId: string
  bucketBalance: number
  bucketCategoryId: number
  bucketCategoryName: string
  bucketCreationDate: string
  bucketNickName?: string
  isCoreSavings: 0 | 1
  updatedByUser: string
  bucketBalanceLastUpdateDate?: string
  bucketGoalAmount?: number
  bucketGoalStartDate?: string
  bucketGoalTargetDate?: string
  monetaryGoalProgress?: number
  remainingGoalAmount?: number
  targetTimeProgress?: number
}
export interface StkBucketCategory {
  categoryDescription: string
  categoryId: number
  categoryName: string
  displayOrder: number
}

export interface StkBucketRules {
  creditSettingsRules: { accountBucketId: string; creditDistribution: number }[]
  debitSettingsRules: {
    accountBucketId: string
    debitPriority: number
  }[]
  interestBucket: {
    accountBucketId: string
  }
}

export interface StkBucketTransaction {
  accountBucketId: string
  accountId: string
  boosterType?: string
  boosterTransactionStatus: string
  bucketBalance: number
  bucketTransactionId: string
  creationDate: string
  groupTransactionId: string
  transactionAmount: string
  transactionDate: string
  transactionDescription: string
  transactionType: string
  transferDate: string
  updatedByUser: string
}

export interface StkBucketActivity {
  accountBucketId: string
  bucketBalance: number
  bucketBalanceLastUpdateDate: string
  bucketCategoryName: string
  transactions: StkBucketTransaction[]
}

export interface StkBoosterTransactionHistory {
  boosterAmount: number
  postedDate: string
  transactionAmount: number
  transactionDescription: string
}

export interface StkBoosterTransaction {
  accruedAmount: number
  boosterTransactionStatus: string
  isAmountTruncated: boolean
  pendingAccrualAmount: number
  transactionGroupId: string
  transactionHistory: StkBoosterTransactionHistory[]
  transferDate: string
}

export interface StkBoosterActivity {
  accountBoosterId: string
  boosterTransactions: StkBoosterTransaction[]
  boosterType: string
  id: string
  lastTransferDate: string
  triggerLimit: number
  triggerAmount: number
}

export interface StkBoosterBucket {
  allocationAmount: number
  bucketCategoryId: number
  bucketCategoryName: string
  bucketNickname?: string
  bucketId: string
  isCoreSavings: 1 | 0
}

export interface StkToolkitAccount {
  accountNickname?: string
  accountType: string
  aggregatorAccountId?: string
  availableBalancePvtEncrypt?: number
  boosterAccountCategory?: 'F' | 'M'
  institutionName?: string
  routingNumber: string
  stkAccountId: string
  accountId: string
  accountNumberPvtEncrypt?: string
  accountStatus: string
  eavStatus: string
  externalAccountIndicator: boolean
  inUse?: boolean
  isAggregatorLinked: boolean
  isAggregatorVerified: boolean
  isFundingEligible: boolean
  isInstitutionSupportedByAggregator: boolean
  isMonitorEligible: boolean
  transferFromIndicator: true
  aggregatorStatus?: string
  bankName: string
  potentialDuplicateAccounts: StkBoosterAccount[]
  ranking?: number // FAS
  potentialSavingsAmount?: number // FAS
}

// This type extends StkToolkitAccounts with extra props to handle authentication and verification
export interface StkToolkitAccountWithEAVProps extends StkToolkitAccount {
  shouldOpenManualModal?: boolean
  patchToolkitAccounts?: boolean
}

export interface StkBoosterAccount {
  accountType: string
  stkAccountId: string
  accountNumber: string
  availableBalancePvtEncrypt?: number
  accountNickname?: string
  boosterAccountCategory?: 'F' | 'M'
  aggregatorAccountId?: string
  aggregatorConnectionId?: string
  aggregatorConnectionStatus: string
  aggregatorStatus: string
  routingNumber: string
  institutionName?: string
  accountStatus: string
}

export interface StkBooster {
  accountBoosterId: string
  accounts: Partial<StkBoosterAccount>[]
  boosterCreationDate: string
  boosterStatus: string
  boosterType: 'FAS' | 'RUP' | 'SIFI'
  buckets: Partial<StkBoosterBucket>[]
  capabilities: string[]
  cumulativeBalance: number
  depositAmount: number
  isEditable: 'Y' | 'N'
  lastUpdatedDate: string
  metadata?: { accruedAmount: number }
  updatedByUser: string
  frequency?: number // SIFI
  nextPaymentDueDate?: string // SIFI
  triggerAmount?: number // RUP
  transactionLimit?: number // RUP
  firstTransferDate?: string // SIFI
}

interface DailyBalance {
  date: string
  accountBalanceOnDate: number
}

export interface Deposits {
  depositType: string
  aggregateDeposit: number
}
export interface MonthlyDepositSummary {
  date: string
  deposits: Deposits[]
}

export interface StkGrowthChartData {
  dailyBalance: DailyBalance[]
  monthlyDepositSummary: MonthlyDepositSummary[]
}

export interface StkPotentialBooster {
  defaultIndicator: boolean
  depositType: string
  enrolledFlag: boolean
  monthlySavings: number
}

export interface StkPotentialChartData {
  averageData: [
    {
      defaultIndicator: boolean
      depositType: string
      enrolledFlag: boolean
      monthlySavings: number
    },
  ]
  monthlyProjections: [
    {
      month: number
      projection: [{ aggregatedSavings: number; depositType: string }]
    },
  ]
  totalProjections: [
    {
      depositType: string
      totalProjections: number
    },
  ]
}

export interface EditStkBucketDetailsFormValues {
  bucketCategory: number
  bucketNickname: string
}

export interface StkBucketUpdates {
  accountBucketId: string | undefined
  bucketCategoryId: number | null
  bucketNickName: string | null
  bucketGoalAmount: number | null
  bucketGoalTargetDate: string | null
}

export interface StkBucketToBeDeleted {
  accountBucketId: string
}
export interface SpendingBucketsTabPreferencesData {
  currentTab?: 'activity' | 'spending' | 'default'
  hasSeenCoachmark?: boolean
}

export type StkTab = 'activity' | 'buckets' | 'boosters'

export interface StkTabPreferencesData {
  currentTab?: StkTab | 'default'
  hasSeenCoachmark?: boolean
}

export interface Preference {
  schemaKey: string
  schemaVersion: string
  data: SpendingBucketsTabPreferencesData | StkTabPreferencesData
}

export interface TransferDetails {
  toBucketId: string
  fromBucketId: string
  amount: number | null
}

export interface PreferenceData {
  schemaVersion: string
  data: SpendingBucketsTabPreferencesData | StkTabPreferencesData
}

export type PreferenceMap = Record<string, PreferenceData>

export enum BucketDeeplinkScenarios {
  Distribute = 'spending-buckets-distribute-money',
  ViewBucket = 'view-spending-bucket',
  ViewUnbucketedTransactions = 'view-unbucketed-transactions',
  CreateBucket = 'create-new-spending-bucket',
  List = 'list',
  OverspentBucket = 'overspent-multiple-buckets',
  Default = 'default',
}

export enum STKDeeplinkScenarios {
  Buckets = 'buckets',
  Boosters = 'boosters',
  RUP = 'rup',
  Default = 'default',
}
export type STKDeeplinkScenario = `${STKDeeplinkScenarios}`

export type STKRedirectLocationState = {
  deeplink: STKDeeplinkScenario
  accountId: string
}

export type BucketDeeplinkScenario = `${BucketDeeplinkScenarios}`

export type SpendBucketsRedirectLocationState = {
  deeplink: BucketDeeplinkScenario
  toolkitBucketId: string
}

export type CoverDraftRedirectLocationState = {
  openCoverDraftSettings: boolean
}

export interface StkBoostersAddNew {
  boosterType: string
  boosterStatus: string
  stkAccountId: string
  bucketId: string
}

export interface RecurringTransfersAddFormValues {
  frequency: number | null
  amount: number | null
  buckets: Record<string, number> | null
  fromAccount: string | null
  firstTransferDate: string
}

export type BoosterUpdate = Partial<StkBooster> &
  Pick<StkBooster, 'boosterType'>

export type BoosterForBuckets = Pick<
  StkBooster,
  'accountBoosterId' | 'boosterType'
>

export interface TransactionDetailsData {
  heading: string | React.ReactElement
  content?: string | number | undefined | React.ReactElement
  isAmount?: boolean
  hidden?: boolean
  isPrivate?: boolean
}

export interface SpendingVisualizationsData {
  month: string
  income: number
  expense: number
}

export interface SpendingVisualizationsResponse {
  monthlyTrends: SpendingVisualizationsData[]
}

export interface SpendingVisualizationsGraphData {
  labels: string[]
  moneyIn: number[]
  moneyOut: number[]
  labelsForTooltip: string[]
}
